package com.iregained.site.components.sections.contact.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.contact.styles.*
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Staggered Pop-In
@Composable
private fun Modifier.staggeredPopIn(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 0.9f).transition(
        Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun ContactRoutingSection(data: ContactRoutingSectionData = defaultContactRoutingData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "contact-routing-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.routes.isNotEmpty()) {
        Column(
            modifier = ContactRoutingContainerStyle.toModifier().id(targetId).fillMaxWidth()
                .backgroundColor(Colors.White)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center).toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.sectionTitle.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionTitle,
                            fontSize = 36.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 16.px).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s)),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    if (data.sectionSubtitle.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().color(palette.custom.darkText)
                                .fontWeight(FontWeight.Medium).textAlign(TextAlign.Center).maxWidth(700.px)
                                .opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s, delay = 150.ms)).toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.sectionSubtitle; onDispose {}
                                    }
                                })
                    }

                    SimpleGrid(numColumns(1, lg = 3), modifier = ContactRoutingGridStyle.toModifier()) {
                        data.routes.forEachIndexed { index, route ->
                            key(route.title) {
                                if (route.targetAnchorId.isNotBlank()) {
                                    Link(
                                        path = route.targetAnchorId,
                                        modifier = Modifier.textDecorationLine(TextDecorationLine.None)
                                            .staggeredPopIn(isVisible, 300 + (index * 150))
                                    ) {
                                        Box(modifier = RoutingCardStyle.toModifier()) {
                                            Column {
                                                if (route.iconPath.isNotBlank()) Box(
                                                    modifier = RoutingIconWrapperStyle.toModifier()
                                                        .align(Alignment.CenterHorizontally),
                                                    contentAlignment = Alignment.Center
                                                ) {
                                                    Image(
                                                        src = route.iconPath,
                                                        alt = route.title,
                                                        modifier = RoutingIconStyle.toModifier()
                                                    )
                                                }
                                                if (route.title.isNotBlank()) SpanText(
                                                    text = route.title,
                                                    modifier = RoutingCardTitleStyle.toModifier()
                                                        .align(Alignment.CenterHorizontally).textAlign(TextAlign.Center)
                                                )
                                                if (route.description.isNotBlank()) Div(
                                                    attrs = RoutingCardDescStyle.toModifier()
                                                        .textAlign(TextAlign.Center).toAttrs {
                                                            ref { htmlElement ->
                                                                htmlElement.innerHTML = route.description; onDispose {}
                                                            }
                                                        },
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL & MEDIA SPECS
// ==========================================
/**
 * CMS IMAGE UPLOAD SPECS FOR ROUTING ICONS:
 * @field iconPath:
 * - Aspect Ratio: 1:1 (Square).
 * - Recommended Dimensions: 64x64px.
 * - Format: SVG.
 * - Content: Simple line-art icons representing a medical building (for admins) and a person/hands (for patients).
 */
data class ContactRouteData(
    val iconPath: String,
    val title: String,
    val description: String,
    val targetAnchorId: String // e.g., "#b2b-form" or "#patient-form" to scroll down
)

data class ContactRoutingSectionData(
    val sectionId: String? = null,
    val sectionTitle: String = "Let's Transform Recovery",
    val sectionSubtitle: String = "To ensure we connect you with the right team of experts, please select the option that best describes you.",
    val routes: List<ContactRouteData>
)

val defaultContactRoutingData = ContactRoutingSectionData(
    sectionId = "contact-routing-section",
    sectionTitle = "Let's Transform Recovery",
    sectionSubtitle = "To ensure we connect you with the right team of experts, please select the option that best describes you.",
    routes = listOf(
        ContactRouteData(
            iconPath = Res.Images.Contact.Route.CLINICIAN,
            title = "I am a Clinician or Administrator",
            description = "Request a hardware demo, discuss facility integration, or inquire about clinical trial data.",
            targetAnchorId = "#contact-b2b-demo-section"
        ), ContactRouteData(
            iconPath = Res.Images.Contact.Route.PATIENT,
            title = "I am a Patient or Caregiver",
            description = "Find the MyHand<sup>®</sup> equipped clinic near you, or get support for your current at-home device.",
            targetAnchorId = "#contact-patient-form-section"
        ),
        // Added 3rd Card for General/Partnerships
        ContactRouteData(
            iconPath = Res.Images.Contact.Support.HEADQUATER,
            title = "General & Partnerships",
            description = "For press, investor relations, career inquiries, or prospective global distributors.",
            targetAnchorId = "#contact-support-section"
        )
    )
)